import { debounce, isEmpty } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
// types and utils
import { CallsConstantsEnum } from 'features/calls/types'
import { focus } from 'utils'
// state
import { errorHandler } from 'utils/errors/errors'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { useTenantPreference } from '../../../settings/settingsHooks'
import { IFileCenterSampleData, UserPreferencesKeysEnum } from '../../../settings/settingsTypes'
import { parseJSON } from '../../../settings/settingsUtils'
import { settingsServiceApiPaths, setUserPreference } from '../../../settings/settingsApi'
import { CsvPreview } from '../CsvPreview/CsvPreview'

const SampleDataForm = () => {
  const { data: fileCenterSampleDataJson, error: fileCenterSampleDataReadError } = useTenantPreference(
    UserPreferencesKeysEnum.fileCenterSettingsSampleData,
  )

  // local state
  const [csvText, setCsvText] = useState<string>('col1,col2,col3\nval1,val2,val3')
  const [fileCenterSampleData, setFileCenterSampleData] = useState<IFileCenterSampleData>()

  useEffect(() => {
    if (fileCenterSampleDataJson?.value) {
      const parsedSampleData = parseJSON(fileCenterSampleDataJson.value)
      if (!isEmpty(parsedSampleData) && typeof parsedSampleData === 'object') {
        const fileCenterSampleDataNew = parsedSampleData as IFileCenterSampleData
        const csvTextNew = fileCenterSampleDataNew.csvText
        setCsvText(csvTextNew)
        setFileCenterSampleData(fileCenterSampleDataNew)
      }
    }
  }, [fileCenterSampleDataJson, fileCenterSampleDataReadError])

  const initialState = { saved: false, loading: false }
  const [state, setState] = useState(initialState)
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    return () => {
      handleChangeDebounce.cancel()
    }
  }, [])

  // handlers
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value !== csvText) {
      setCsvText(e.target.value)
      if (error) setError(false)

      // Update local state
      setState((prev) => ({
        ...prev,
        saved: false,
        loading: false,
      }))

      handleChangeDebounce(e.target.value)
    }
  }
  const handleSave = async (csvText: string) => {
    // Set loading true
    setState((prev) => ({
      ...prev,
      saved: false,
      loading: true,
    }))
    if (error) setError(false)

    try {
      const fileCenterSampleDataNew = {
        ...fileCenterSampleData,
        csvText: csvText,
      } as IFileCenterSampleData
      setFileCenterSampleData(fileCenterSampleDataNew)

      await setUserPreference(settingsServiceApiPaths.tenantPreferencesPut(), {
        key: UserPreferencesKeysEnum.fileCenterSettingsSampleData,
        value: JSON.stringify(fileCenterSampleDataNew),
      })

      setState((prev) => ({
        ...prev,
        saved: true,
        loading: false,
      }))

      // Focus textarea element
      focus('edit-sample-data')
    } catch (error: any) {
      errorHandler.handleError(error, ErrorMessagesEnum.SettingsSampleData)
      setError(true)
    } finally {
      // set the state back after delay
      setTimeout(() => setState(initialState), 500)
    }
  }

  const handleChangeDebounce = useCallback(
    debounce((notes) => handleSave(notes), CallsConstantsEnum.timerTimeout),
    [],
  )

  return (
    <Div>
      <div className="edit-or-paste-container">
        <div className="text-preview">
          <h3 className="fit">Edit or paste sample data</h3>
          <div className="grow">
            <textarea
              className="grey-scrollbar"
              name="edit-sample-data"
              id="edit-sample-data"
              value={csvText}
              onChange={handleChange}
              placeholder="Click here to enter sample CSV data"
              data-testid="SampleDataTextarea"
              wrap="off"
              spellCheck="false"
            ></textarea>
          </div>
          <div className="label">
            {state.saved && 'Saved'}
            {state.loading && 'Saving...'}
          </div>
          <div className="notes-message fit">Sample Data are saved automatically after a short delay.</div>
        </div>
      </div>

      <div className="preview-container">
        {state.loading ? (
          <div>Loading...</div>
        ) : (
          <CsvPreview
            columnSettings={[]}
            fileCenterSampleData={{ csvText: csvText } as IFileCenterSampleData}
            justSniff={true}
          />
        )}
      </div>
    </Div>
  )
}

const Div = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 0.5rem;
  border: none;

  .edit-or-paste-container {
    height: 30%;
    width: 100%;
    overflow: hidden;
  }

  .preview-container {
    height: 70%;
  }

  .text-preview {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  .fit {
    height: fit-content;
  }

  .grow {
    flex-grow: 1;
  }

  textarea {
    height: 100%;
    width: 100%;
    color: ${({ theme }) => theme.colors.grey.neutral};
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    border-radius: 0.5rem;
    border: solid;
    border-width: 0.07rem;
    border-color: ${({ theme }) => theme.colors.grey.light};
    padding: 1rem;
    margin-bottom: 0.5rem;
    resize: none;

    &:focus {
      outline: none;
    }

    &.hasError {
      box-shadow: ${({ theme }) => theme.colors.error.light};
    }
  }

  .label {
    position: absolute;
    top: 0.5rem;
    right: 2.25rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    ${({ theme }) => theme.typography.s.normal};
    font-style: italic;
    color: ${({ theme }) => theme.colors.primary.dark};
  }
  .notes-message {
    margin: 0.5rem;
    align-self: center;
    ${({ theme }) => theme.typography.s.normal};
    color: ${({ theme }) => theme.colors.grey.light};
  }
`

export { SampleDataForm }
